import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';
import { activeShopHeaders } from 'src/utils/shopContext';
import { createTeamPresenceConnection } from 'src/utils/teamPresenceConnection';
import { mergeTeamPresence } from 'src/utils/teamDirectoryPresence';
import { socketClient } from '../index';

const MESSAGE_PAGE_SIZE = 50;

const realtime = {
  socket: null,
  stop: null,
  handlers: {}
};

const authHeaders = async () => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Authentication is still loading. Please retry.');
  return {
    Authorization: `Bearer ${await user.getIdToken()}`,
    ...activeShopHeaders()
  };
};

const api = async (method, path, data, config = {}) => axios({
  method,
  url: buildApiUrl(`/api/team${path}`),
  data,
  timeout: 15000,
  ...config,
  headers: {
    ...(await authHeaders()),
    ...(config.headers || {})
  }
});

const errorMessage = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

const uniqueMessages = (messages) => Array.from(
  new Map((messages || []).filter((item) => item?.id).map((item) => [String(item.id), item])).values()
).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

const displayConversation = (conversation, viewer) => {
  if (!conversation || conversation.kind !== 'direct') return conversation;
  const other = (conversation.participants || []).find(
    (participant) => participant.actorKey !== viewer?.actorKey
  );
  return { ...conversation, name: other?.displayName || conversation.name || 'Direct message' };
};

const unreadTotal = (employees, groups) => [
  ...(employees || []),
  ...(groups || [])
].reduce((total, entry) => total + Number(entry.unreadCount || 0), 0);

const initialState = {
  viewer: null,
  employees: [],
  groups: [],
  generatedAt: null,
  directoryLoading: false,
  directoryError: null,
  directoryRequestId: null,
  pendingPresence: {},
  unreadCount: 0,
  activeConversation: null,
  messages: [],
  messagesLoading: false,
  messagesRequestId: null,
  messagesError: null,
  nextCursor: null,
  hasMore: false,
  loadingMore: false,
  sending: false,
  sendError: null,
  typingByActor: {},
  groupSaving: false,
  groupError: null
};

export const requestTeamDirectory = createAsyncThunk(
  'teamChat/requestDirectory',
  async (_, { rejectWithValue }) => {
    try {
      return (await api('get', '/directory')).data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to load the team directory.'));
    }
  },
  { condition: (_, { getState }) => !getState().teamChat.directoryRequestId }
);

export const requestTeamMessages = createAsyncThunk(
  'teamChat/requestMessages',
  async ({ conversation, append = false }, { getState, rejectWithValue }) => {
    try {
      const state = getState().teamChat;
      const cursor = append ? state.nextCursor : null;
      const response = await api('get', `/conversations/${encodeURIComponent(conversation.id)}/messages`, null, {
        params: { limit: MESSAGE_PAGE_SIZE, ...(cursor ? { before: cursor } : {}) }
      });
      return { ...response.data, append, requestedConversation: conversation };
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to load this team conversation.'));
    }
  }
);

export const openTeamDirect = createAsyncThunk(
  'teamChat/openDirect',
  async (employee, { dispatch, rejectWithValue }) => {
    try {
      const response = await api('post', '/conversations/direct', {
        employeeId: employee.id === 'tower' ? undefined : employee.id,
        actorKey: employee.id === 'tower' ? 'tower' : undefined
      });
      const conversation = response.data.conversation;
      await dispatch(requestTeamMessages({ conversation })).unwrap();
      dispatch(requestTeamDirectory());
      return conversation;
    } catch (error) {
      return rejectWithValue(
        typeof error === 'string' ? error : errorMessage(error, 'Unable to open this conversation.')
      );
    }
  }
);

export const openTeamGroup = (group) => async (dispatch) => {
  const conversation = { ...group, kind: 'group' };
  return dispatch(requestTeamMessages({ conversation }));
};

export const sendTeamMessage = createAsyncThunk(
  'teamChat/sendMessage',
  async ({ conversationId, body, attachments = [] }, { rejectWithValue }) => {
    try {
      const response = await api('post', `/conversations/${encodeURIComponent(conversationId)}/messages`, {
        body,
        attachments,
        clientMessageId: typeof window.crypto?.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to send this message.'));
    }
  }
);

export const uploadTeamAttachment = createAsyncThunk(
  'teamChat/uploadAttachment',
  async (file, { rejectWithValue }) => {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api('post', '/attachments', form, { timeout: 60000 });
      return response.data.attachment;
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to upload this attachment.'));
    }
  }
);

export const markTeamConversationRead = createAsyncThunk(
  'teamChat/markRead',
  async (conversationId, { rejectWithValue }) => {
    try {
      await api('post', `/conversations/${encodeURIComponent(conversationId)}/read`);
      return conversationId;
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to mark the conversation read.'));
    }
  }
);

export const createTeamGroup = createAsyncThunk(
  'teamChat/createGroup',
  async (input, { dispatch, rejectWithValue }) => {
    try {
      const conversation = (await api('post', '/conversations/groups', input)).data.conversation;
      dispatch(requestTeamDirectory());
      return conversation;
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to create the group.'));
    }
  }
);

export const updateTeamGroup = createAsyncThunk(
  'teamChat/updateGroup',
  async ({ conversationId, ...input }, { dispatch, rejectWithValue }) => {
    try {
      const conversation = (await api(
        'patch',
        `/conversations/${encodeURIComponent(conversationId)}`,
        input
      )).data.conversation;
      dispatch(requestTeamDirectory());
      return conversation;
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to update the group.'));
    }
  }
);

export const archiveTeamGroup = createAsyncThunk(
  'teamChat/archiveGroup',
  async (conversationId, { dispatch, rejectWithValue }) => {
    try {
      await api('delete', `/conversations/${encodeURIComponent(conversationId)}`);
      dispatch(requestTeamDirectory());
      return conversationId;
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Unable to archive the group.'));
    }
  }
);

const slice = createSlice({
  name: 'teamChat',
  initialState,
  reducers: {
    clearActiveTeamConversation(state) {
      state.activeConversation = null;
      state.messagesRequestId = null;
      state.messages = [];
      state.messagesError = null;
      state.typingByActor = {};
    },
    teamMessageReceived(state, action) {
      const { message, conversation: rawConversation } = action.payload || {};
      if (!message?.id || !rawConversation?.id) return;
      const conversation = displayConversation(rawConversation, state.viewer);
      const ownMessage = message.sender?.actorKey === state.viewer?.actorKey;
      if (state.activeConversation?.id === conversation.id) {
        state.messages = uniqueMessages([...state.messages, message]);
      }
      const collection = conversation.kind === 'group' ? state.groups : state.employees;
      const entry = conversation.kind === 'group'
        ? collection.find((item) => item.id === conversation.id)
        : collection.find((item) => (conversation.participants || []).some(
          (participant) => participant.employeeId === item.id || (item.id === 'tower' && participant.actorKey === 'tower')
        ));
      if (entry) {
        entry.lastMessage = message.body || (message.attachments?.length ? 'Attachment' : 'New message');
        entry.lastMessageAt = message.createdAt;
        if (!ownMessage && state.activeConversation?.id !== conversation.id) {
          entry.unreadCount = Number(entry.unreadCount || 0) + 1;
        }
      }
      state.unreadCount = unreadTotal(state.employees, state.groups);
    },
    teamPresenceChanged(state, action) {
      const id = action.payload?.employeeId;
      if (id == null) return;
      if (state.directoryRequestId) {
        state.pendingPresence[String(id)] = { ...state.pendingPresence[String(id)], ...action.payload };
      }
      const employee = state.employees.find((item) => String(item.id) === String(action.payload?.employeeId));
      if (employee) {
        Object.assign(employee, mergeTeamPresence(employee, action.payload));
      }
    },
    teamRealtimeStopped(state) {
      state.directoryRequestId = null;
      state.pendingPresence = {};
    },
    teamConversationChanged(state, action) {
      const conversation = displayConversation(action.payload?.conversation, state.viewer);
      if (!conversation?.id) return;
      if (conversation.kind === 'group') {
        const existing = state.groups.find((item) => item.id === conversation.id);
        if (existing) Object.assign(existing, conversation);
        else state.groups.unshift(conversation);
      }
      if (state.activeConversation?.id === conversation.id) state.activeConversation = conversation;
    },
    teamConversationArchived(state, action) {
      const id = String(action.payload?.conversationId || '');
      state.groups = state.groups.filter((item) => item.id !== id);
      if (state.activeConversation?.id === id) {
        state.activeConversation = null;
        state.messages = [];
      }
      state.unreadCount = unreadTotal(state.employees, state.groups);
    },
    teamTypingChanged(state, action) {
      const payload = action.payload || {};
      if (!payload.actorKey || payload.actorKey === state.viewer?.actorKey) return;
      if (payload.isTyping) state.typingByActor[payload.actorKey] = payload.displayName || 'Someone';
      else delete state.typingByActor[payload.actorKey];
    },
    clearTeamSendError(state) {
      state.sendError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestTeamDirectory.pending, (state, action) => {
        state.directoryRequestId = action.meta.requestId;
        state.pendingPresence = {};
        state.directoryLoading = state.employees.length === 0 && state.groups.length === 0;
        state.directoryError = null;
      })
      .addCase(requestTeamDirectory.fulfilled, (state, action) => {
        if (state.directoryRequestId !== action.meta.requestId) return;
        state.directoryError = action.payload.phoneDirectory?.status === 'unavailable'
          ? 'Phone availability is unknown. Please retry.' : null;
        state.viewer = action.payload.viewer;
        state.employees = (action.payload.employees || []).map(employee =>
          mergeTeamPresence(employee, state.pendingPresence[String(employee.id)]));
        state.directoryRequestId = null;
        state.pendingPresence = {};
        state.groups = action.payload.groups || [];
        state.generatedAt = action.payload.generatedAt || null;
        state.unreadCount = unreadTotal(state.employees, state.groups);
        state.directoryLoading = false;
      })
      .addCase(requestTeamDirectory.rejected, (state, action) => {
        if (state.directoryRequestId !== action.meta.requestId) return;
        state.directoryRequestId = null;
        state.pendingPresence = {};
        state.directoryLoading = false;
        state.directoryError = action.payload || 'Unable to load the team directory.';
        state.employees = state.employees.map(employee => employee.isTower ? employee : ({
          ...employee, presence: 'unknown', callState: 'unknown', acceptingCalls: false,
          transferEligible: false, lastSeenAt: null,
          transferBlockedReason: 'Phone availability is temporarily unavailable.'
        }));
      })
      .addCase(requestTeamMessages.pending, (state, action) => {
        state.messagesRequestId = action.meta.requestId;
        if (action.meta.arg.append) state.loadingMore = true;
        else {
          state.messagesLoading = true;
          state.messages = [];
          state.typingByActor = {};
          state.sendError = null;
          state.loadingMore = false;
          state.activeConversation = action.meta.arg.conversation;
        }
        state.messagesError = null;
      })
      .addCase(requestTeamMessages.fulfilled, (state, action) => {
        if (state.messagesRequestId !== action.meta.requestId) return;
        state.messagesRequestId = null;
        const conversation = displayConversation(
          action.payload.conversation || action.payload.requestedConversation,
          state.viewer
        );
        state.activeConversation = conversation;
        state.messages = uniqueMessages([...(action.payload.messages || []), ...state.messages]);
        state.nextCursor = action.payload.nextCursor || null;
        state.hasMore = Boolean(action.payload.hasMore);
        state.messagesLoading = false;
        state.loadingMore = false;
      })
      .addCase(requestTeamMessages.rejected, (state, action) => {
        if (state.messagesRequestId !== action.meta.requestId) return;
        state.messagesRequestId = null;
        state.messagesLoading = false;
        state.loadingMore = false;
        state.messagesError = action.payload || 'Unable to load this team conversation.';
      })
      .addCase(sendTeamMessage.pending, (state) => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendTeamMessage.fulfilled, (state, action) => {
        if (action.payload?.message) state.messages = uniqueMessages([...state.messages, action.payload.message]);
        state.sending = false;
      })
      .addCase(sendTeamMessage.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.payload || 'Unable to send this message.';
      })
      .addCase(markTeamConversationRead.fulfilled, (state, action) => {
        const id = String(action.payload);
        state.groups.forEach((entry) => { if (entry.id === id) entry.unreadCount = 0; });
        state.employees.forEach((entry) => { if (entry.conversationId === id) entry.unreadCount = 0; });
        state.unreadCount = unreadTotal(state.employees, state.groups);
      })
      .addCase(createTeamGroup.pending, (state) => { state.groupSaving = true; state.groupError = null; })
      .addCase(createTeamGroup.fulfilled, (state) => { state.groupSaving = false; })
      .addCase(createTeamGroup.rejected, (state, action) => { state.groupSaving = false; state.groupError = action.payload; })
      .addCase(updateTeamGroup.pending, (state) => { state.groupSaving = true; state.groupError = null; })
      .addCase(updateTeamGroup.fulfilled, (state) => { state.groupSaving = false; })
      .addCase(updateTeamGroup.rejected, (state, action) => { state.groupSaving = false; state.groupError = action.payload; })
      .addCase(archiveTeamGroup.fulfilled, (state, action) => {
        state.groups = state.groups.filter((item) => item.id !== action.payload);
        if (state.activeConversation?.id === action.payload) state.activeConversation = null;
      });
  }
});

export const initializeTeamRealtime = () => async (dispatch) => {
  const socket = socketClient.socket;
  if (!socket || realtime.socket === socket) return;
  dispatch(cleanupTeamRealtime());
  realtime.socket = socket;
  realtime.handlers.message = (payload) => dispatch(slice.actions.teamMessageReceived(payload));
  realtime.handlers.presence = (payload) => dispatch(slice.actions.teamPresenceChanged(payload));
  realtime.handlers.updated = (payload) => {
    dispatch(slice.actions.teamConversationChanged(payload));
    dispatch(requestTeamDirectory());
  };
  realtime.handlers.archived = (payload) => dispatch(slice.actions.teamConversationArchived(payload));
  realtime.handlers.typing = (payload) => dispatch(slice.actions.teamTypingChanged(payload));
  socket.on('team:message:new', realtime.handlers.message);
  socket.on('team:presence:changed', realtime.handlers.presence);
  socket.on('team:conversation:updated', realtime.handlers.updated);
  socket.on('team:conversation:archived', realtime.handlers.archived);
  socket.on('team:typing', realtime.handlers.typing);
  realtime.stop = createTeamPresenceConnection({
    socket, document, window,
    refresh: () => dispatch(requestTeamDirectory()),
    reconnect: async () => {
      const user = firebase.auth().currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      if (realtime.socket === socket && socketClient.socket === socket) {
        await socketClient.connect(token, socketClient.shopId);
      }
    }
  });
};

export const cleanupTeamRealtime = () => (dispatch) => {
  realtime.stop?.();
  Object.entries(realtime.handlers).forEach(([key, handler]) => {
    const events = {
      message: 'team:message:new', presence: 'team:presence:changed', updated: 'team:conversation:updated',
      archived: 'team:conversation:archived', typing: 'team:typing'
    };
    realtime.socket?.off(events[key], handler);
  });
  realtime.socket = null;
  realtime.stop = null;
  realtime.handlers = {};
  dispatch(slice.actions.teamRealtimeStopped());
};

export const emitTeamTyping = (conversationId, isTyping) => {
  if (!conversationId) return;
  socketClient.emitWithAck('team:typing', { conversationId, isTyping }, 3000).catch(() => undefined);
};

export const {
  clearActiveTeamConversation,
  clearTeamSendError
} = slice.actions;

export const reducer = slice.reducer;
